<template>
  <v-app-bar
    class="app-bar"
    :order="2"
    :elevation="0"
    color="white"
    data-locator="app-bar"
    :extension-height="ROOM_HEADER_EXTENSION_HEIGHT"
  >
    <template v-if="!isSearchOpen">
      <v-app-bar-nav-icon
        v-if="$vuetify.display.mobile && (homeLayout || projectLayout) && fullAccessScope"
        @click="SET_TOGGLE_PROJECT_DRAWER(true)"
      >
        <app-icon-ds
          icon="fa-bars"
          weight="fas"
          size="md"
        />
      </v-app-bar-nav-icon>
      <v-app-bar-nav-icon
        v-if="$vuetify.display.mobile && isInProfileLayout && fullAccessScope"
        @click="openProfileDrawer"
      >
        <v-img :src="appBarLogo"
               width="72"
               transition="scale-transition"
               alt="Logo"
        />
      </v-app-bar-nav-icon>

      <div class="app-bar-layout">
        <h2 class="app-bar-left" :class="$vuetify.display.mobile  ? 'pl-4' : 'pl-8'">
          <div v-if="projectLayout && !$vuetify.display.mobile && roomName && !shouldDisplayRoomBreadcrumb"
               class="d-flex align-center"
          >
            <img
              :src="projectCategoryIcon"
              width="24"
              alt="room-category-icon"
            />
            <AppTooltip location="bottom">
              <template v-slot:activator="{ props }">
                <span class="room-name text-truncate ml-2" v-bind="props">
                  {{ roomName }}
                </span>
              </template>
              {{ roomName }}
            </AppTooltip>
          </div>

          <router-link v-if="$vuetify.display.mobile && fullAccessScope"
                       :to="{ name: routes.ROOMS_ROUTE_NAME }"
                       class="pa-2 d-block"
                       data-locator="back-to-dashboard-button"
          >
            <v-img
              :src="appBarLogo"
              :width="hasSubscriptionLogo ? 40 : 100"
              alt="Logo"
            />
          </router-link>

          <template v-if="!$vuetify.display.mobile && !projectLayout && fullAccessScope">
            <router-link
              v-if="!homeLayout"
              :to="{ name: routes.ROOMS_ROUTE_NAME }"
              class="pa-2 d-flex flex-column align-center text-decoration-none"
              data-locator="back-to-dashboard-button"
            >
              <v-img
                alt="Closd Name"
                class="shrink AppBar-closd-logo"
                min-width="100"
                src="@/assets/img/closd_logo.svg"
                width="100"
              />

              <p class="text-body-2 mb-0 mt-1 text-grey-800">
                {{ $t('common.app-bar.AppBar.backToHome') }}
              </p>
            </router-link>
          </template>

          <v-img
            v-if="!fullAccessScope && $vuetify.display.mobile"
            alt="Closd Logo"
            src="@/assets/img/closd_logo.svg"
            transition="scale-transition"
            :width="72"
          />

          <v-img v-if="!fullAccessScope && !$vuetify.display.mobile && !projectLayout"
                alt="Closd Name"
                class="shrink mt-1"
                min-width="100"
                src="@/assets/img/closd_logo.svg"
                width="100"
          />
        </h2>

        <div v-if="fullAccessScope" class="app-bar-center">
          <AppBarDocumentSearch v-if="!$vuetify.display.xs && shouldDisplayGlobalSearch" />
        </div>

        <div class="app-bar-right"
             :style="$vuetify.display.xs ? {} : { gap: '4px' }"
             :class="$vuetify.display.mobile  ? 'pr-4' : 'pr-8'"
        >
          <app-button
            v-if="$vuetify.display.xs && shouldDisplayGlobalSearch"
            class="mx-4"
            type='icon'
            size="small"
            @click="isSearchOpen = true"
          >
            <app-icon-ds icon="search" size="sm" />
          </app-button>

          <ProjectsMenuItem v-if="fullAccessScope" />

          <AppTooltip v-if="!$vuetify.display.mobile">
            <template v-slot:activator="{ props }">
              <AppBarMenuItem v-if="fullAccessScope && profile.subscription && !profile.subscription?.hideArchiveOption"
                              v-bind="props"
                              v-slot="{active}"
                              :active="currentRoute.name.includes(ARCHIVES_ROUTE_NAME)"
                              @click="onArchivesClick"
              >
                <ArchiveAddAccessIcon :isArchivingEnabled="isArchivingEnabled" :solid="active"/>
              </AppBarMenuItem>
            </template>
            {{ $t('common.app-bar.AppBar.archiving') }}
          </AppTooltip>

          <HelpMenuItem v-if="!$vuetify.display.mobile"/>

          <ProfileMenuItem :fullAccessScope="fullAccessScope"/>
        </div>
      </div>
    </template>

    <template v-if="shouldDisplayGlobalSearch && isSearchOpen">
      <app-button
        class="mx-3"
        type='icon'
        size="small"
        @click="isSearchOpen = false"
      >
        <app-icon-ds icon="arrow-left" size="sm" />
      </app-button>
      <AppBarDocumentSearch />
    </template>

    <template v-if="shouldDisplayRoomBreadcrumb" #extension>
      <div class="breadcrumb-bar w-100 d-flex align-center py-3 px-4">
        <AppBarRoomBreadcrumb :room="currentRoom" :currentNavigationPage="currentRoomNavigationPage" />
      </div>
    </template>
  </v-app-bar>
</template>

<script>
import { mapGetters, mapState, mapMutations } from 'vuex'

import closdLogo from '@/assets/img/closd_logo.svg'
import AppBarMenuItem from '@/common/app-bar/AppBarMenuItem.vue'
import AppBarRoomBreadcrumb from '@/common/app-bar/AppBarRoomBreadcrumb.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import ArchiveAddAccessIcon from '@/common/archives/ArchiveAddAccessIcon.vue'
import { ROOM_HEADER_EXTENSION_HEIGHT } from '@/common/constants.js'
import { getRoomNavigationDataFromRoute } from '@/common/navigation-drawer/roomNavigationData.js'
import { getRoomIcon } from '@/common/utils/rooms'
import {
  HOME_LAYOUT_ROUTE_NAMES,
  ROOMS_ROUTE_NAME,
  ROOM_ROUTE_TYPES,
  PROFILE_ROUTE_NAME,
  ARCHIVES_ROUTE_NAME,
  getCurrentRouteSafe,
} from '@/router'
import { ArchivesMutationTypes } from '@/store/modules/archives/mutation_types'
import { GlobalMutationTypes } from '@/store/modules/global/mutation_types'

import AppBarDocumentSearch from './AppBarDocumentSearch.vue'
import HelpMenuItem from './HelpMenuItem.vue'
import ProfileMenuItem from './ProfileMenuItem.vue'
import ProjectsMenuItem from './ProjectsMenuItem.vue'

export default {
  name: 'AppBar',
  components: {
    AppBarDocumentSearch,
    AppBarMenuItem,
    AppBarRoomBreadcrumb,
    AppTooltip,
    ArchiveAddAccessIcon,
    HelpMenuItem,
    ProfileMenuItem,
    ProjectsMenuItem,
  },
  props: {
    fullAccessScope: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      isSearchOpen: false,
      ARCHIVES_ROUTE_NAME,
      tab: 1,
      item: 0,
      discoverDialogIsOpen: false,
      ROOM_HEADER_EXTENSION_HEIGHT,
    }
  },
  computed: {
    ...mapState('global', ['homeLayout', 'projectLayout']),
    ...mapState('documents', ['searchFilesPending']),
    ...mapState('room', ['currentRoom']),
    ...mapGetters('room', ['roomName']),
    ...mapGetters('user', ['isArchivingEnabled']),
    ...mapState('user', ['profile']),
    ...mapState('documentsBreadcrumb', ['breadcrumb']),
    ...mapGetters('subscription', ['getSubscriptionLogo']),
    // We use this one because we are init even before the router is properly finished,
    // since the app can deduce the layout before then and we are at the top level
    currentRoute () {
      return getCurrentRouteSafe()
    },
    currentRoomNavigationPage () {
      return getRoomNavigationDataFromRoute(this.currentRoute)
    },
    shouldDisplayRoomBreadcrumb () {
      return this.currentRoom && !!this.currentRoomNavigationPage && this.currentRoute.name !== ROOMS_ROUTE_NAME
    },
    hasSubscriptionLogo () {
      return !!this.getSubscriptionLogo
    },
    appBarLogo () {
      return this.getSubscriptionLogo || closdLogo
    },
    shouldDisplayGlobalSearch () {
      const { name, params } = this.currentRoute
      return (
        HOME_LAYOUT_ROUTE_NAMES.includes(name) ||
        [ROOM_ROUTE_TYPES.PROJECT, ROOM_ROUTE_TYPES.DATAROOM].includes(params.roomType)
      )
    },
    routes () {
      return {
        ROOMS_ROUTE_NAME,
      }
    },
    isInProfileLayout() {
      return this.currentRoute?.name?.includes(PROFILE_ROUTE_NAME)
    },
    projectCategoryIcon() {
      let src = ''
      if (this.projectLayout && this.currentRoom) {
        src = getRoomIcon(this.currentRoom)
      }
      return src
    },
  },
  created() {
    this.ROOMS_ROUTE_NAME = ROOMS_ROUTE_NAME
    this.ARCHIVES_ROUTE_NAME = ARCHIVES_ROUTE_NAME
  },
  methods: {
    ...mapMutations('global', [GlobalMutationTypes.SET_TOGGLE_PROJECT_DRAWER]),
    ...mapMutations('archives', [ArchivesMutationTypes.SET_DISCOVER_ELECTRONIC_SAFE_DIALOG_IS_OPEN]),
    openProfileDrawer () {
      this.$store.commit('global/' + GlobalMutationTypes.SET_NAVIGATION_DRAWER_PROFILE, true)
    },
    onArchivesClick () {
      if (this.isArchivingEnabled) {
        this.$router.push({ name: ARCHIVES_ROUTE_NAME })
      } else {
        this.SET_DISCOVER_ELECTRONIC_SAFE_DIALOG_IS_OPEN(true)
      }
    },
  },
}
</script>

<style scoped lang="scss">
.app-bar {
  border-bottom: 1px solid rgb(var(--v-theme-grey-100));
}

.breadcrumb-bar {
  height: 52px;
  border-top: 1px solid rgb(var(--v-theme-grey-100));
}

.app-bar-layout {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  height: 100%;
}

.app-bar-left {
  flex: 0 0 25%;
  min-width: 0;
}

.app-bar-center {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
}

.app-bar-right {
  flex: 0 0 25%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
  height: 100%;
}

:deep(.v-toolbar__content) {
  overflow: visible;

  .v-btn:first-child {
    margin-inline-start: 0px;
  }
}
</style>
